/**
 * Saved (favourited) listings, browser side.
 *
 * The set of listings THIS browser saved stays in local storage, which is what
 * keeps the heart filled between visits without an account. The aggregate
 * count lives in the database, so a toggle is also sent to the API keyed by an
 * opaque visitor id that is generated here and never leaves the browser in any
 * other form.
 *
 * Every function in this file touches `window`, so none of them may be called
 * during render or from a `useState` initialiser. Call them from an effect or
 * an event handler only.
 */

const STORAGE_KEY = 'mta:saved-listings'
const VISITOR_KEY = 'mta:visitor-id'

/** Fired on `window` whenever a listing is saved or unsaved on this page. */
export const FAVORITE_EVENT = 'mta:favorite-change'

export type FavoriteChange = {
    listingId: string
    saved: boolean
}

export const readSavedListings = (): Array<string> => {
    try {
        const raw = window.localStorage.getItem(STORAGE_KEY)
        const parsed = raw ? JSON.parse(raw) : []

        return Array.isArray(parsed) ? parsed.filter((item): item is string => typeof item === 'string') : []
    } catch {
        return []
    }
}

const readVisitorId = (): string => {
    try {
        const existing = window.localStorage.getItem(VISITOR_KEY)

        if (existing) return existing

        const created = typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
            ? crypto.randomUUID()
            : `v${Date.now().toString(36)}${Math.random().toString(36).slice(2, 12)}`

        window.localStorage.setItem(VISITOR_KEY, created)

        return created
    } catch {
        return ''
    }
}

/**
 * Toggles the listing, persists the new set, tells the server so the public
 * count stays true, and announces the change so any count on the same page
 * updates immediately. Returns the new saved state.
 */
export const toggleSavedListing = (listingId: string): boolean => {
    const current = readSavedListings()
    const saved = !current.includes(listingId)
    const next = saved ? [...current, listingId] : current.filter((item) => item !== listingId)

    try {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next))
    } catch {
        /* Storage can be unavailable in private mode. The UI still updates. */
    }

    const visitorId = readVisitorId()

    if (visitorId) {
        void fetch('/api/listings/favorite', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ listingId, visitorId, saved }),
            keepalive: true
        }).catch(() => {
            /* The local state is still correct; the count catches up next time. */
        })
    }

    window.dispatchEvent(new CustomEvent<FavoriteChange>(FAVORITE_EVENT, { detail: { listingId, saved } }))

    return saved
}
